import { readAssistantStream } from './assistantStream'
import { uploadDirect } from './multipartUpload'
import { transferUpload } from './uploadTransfer'
import { AUTH_EXPIRED_EVENT, shouldNotifyAuthExpiry } from './authRecovery'
import type {
  Bootstrap,
  CaptureConfig,
  CaptureCapabilities,
  DeckLinkInput,
  CaptureMode,
  Clip,
  Comment,
  Delivery,
  Event,
  Job,
  Settings,
  SproutReceipt,
  PreparedProjectInspection,
  PreparedProjectRegistration,
  PreparedProjectRegistrationInput,
  Source,
  Transcript,
  User,
  AssistantConversation,
  AssistantEvent,
  LiveMonitor,
  MulticamProject,
  EditorialPlan, EditContext, SourceMoment,
} from './types'

export class ApiRequestError extends Error {
  status: number
  code?: string
  retryAfter?: number
  constructor(message: string, status: number, options: { code?: string; retryAfter?: number } = {}) {
    super(message)
    this.name = 'ApiRequestError'
    this.status = status
    this.code = options.code
    this.retryAfter = options.retryAfter
  }
}

/** Emitted when an authenticated request proves that the current session is gone. */
export { AUTH_EXPIRED_EVENT }

const unauthenticatedPaths = new Set([
  '/api/bootstrap',
  '/api/setup',
  '/api/login',
  '/api/logout',
  '/api/auth/code',
  '/api/auth/verify',
  '/api/auth/session',
])

let sessionVerification: Promise<boolean> | null = null

/**
 * A forbidden operation is normally a valid session without the required
 * role. Verify the session before treating it as an authentication boundary;
 * a provider or network failure remains an ordinary operation error.
 */
async function sessionWasRevoked(): Promise<boolean> {
  if (sessionVerification) return sessionVerification
  sessionVerification = fetch('/api/me', { credentials: 'include', headers: { Accept: 'application/json' } })
    .then((response) => response.status === 401 || response.status === 403)
    .catch(() => false)
    .finally(() => { sessionVerification = null })
  return sessionVerification
}

async function notifyAuthExpiry(path: string, response: Response) {
  if (typeof window === 'undefined' || unauthenticatedPaths.has(path.split('?')[0])) return
  if (shouldNotifyAuthExpiry(response.status, null)) {
    window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT))
    return
  }
  const verifiedSessionStatus = response.status === 403 ? (path.split('?')[0] === '/api/me' ? 403 : (await sessionWasRevoked() ? 403 : null)) : null
  if (shouldNotifyAuthExpiry(response.status, verifiedSessionStatus)) {
    window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT))
  }
}

function errorDetails(payload: unknown): { message: string; code?: string } {
  if (typeof payload === 'string' && payload.trim()) return { message: payload.trim() }
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return { message: '' }
  const value = payload as { detail?: unknown; error?: unknown; message?: unknown; code?: unknown }
  const detail = value.detail
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const nested = errorDetails(detail)
    if (nested.message || nested.code) return nested
  }
  if (typeof detail === 'string' && detail.trim()) return { message: detail.trim(), ...(typeof value.code === 'string' ? { code: value.code } : {}) }
  if (value.error && typeof value.error === 'object' && !Array.isArray(value.error)) {
    const nested = errorDetails(value.error)
    if (nested.message || nested.code) return nested
  }
  if (typeof value.error === 'string' && value.error.trim()) return { message: value.error.trim(), ...(typeof value.code === 'string' ? { code: value.code } : {}) }
  if (typeof value.message === 'string' && value.message.trim()) return { message: value.message.trim(), ...(typeof value.code === 'string' ? { code: value.code } : {}) }
  return { message: '', ...(typeof value.code === 'string' ? { code: value.code } : {}) }
}

function retryAfterSeconds(response: Response) {
  const raw = response.headers.get('retry-after')
  if (raw === null) return undefined
  const seconds = Number(raw)
  return Number.isFinite(seconds) && seconds >= 0 && seconds <= 3600 ? Math.ceil(seconds) : undefined
}

async function request<T>(path: string, init: RequestInit = {}, onProgress?: (fraction: number) => void): Promise<T> {
  const headers = new Headers(init.headers)
  if (init.body && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json')
  const options: RequestInit = { ...init, headers, credentials: 'include' }
  const response = onProgress || (init.signal && init.body instanceof FormData) ? await transferUpload(path, options, onProgress) : await fetch(path, options)
  const raw = await response.text()
  let payload: unknown = null
  if (raw) {
    try {
      payload = JSON.parse(raw)
    } catch {
      payload = raw
    }
  }
  if (!response.ok) {
    await notifyAuthExpiry(path, response)
    const details = errorDetails(payload)
    const detail = details.message || `Request failed (${response.status})`
    throw new ApiRequestError(detail, response.status, { code: details.code, retryAfter: retryAfterSeconds(response) })
  }
  // A successful mutation invalidates shared in-flight inventories too.
  // Otherwise the view's post-save refresh can join a pre-save read and
  // resurrect deleted clips despite its own request-order fencing.
  const method = (init.method || 'GET').toUpperCase()
  // Status uses POST to carry bounded ID lists, but does not mutate work.
  const readsJobStatus = method === 'POST' && path === '/api/jobs/status'
  if (!['GET', 'HEAD'].includes(method) && !readsJobStatus) clearClipListCache()
  return payload as T
}

type ClipListParams = { status?: string; q?: string; source_id?: string }
type ClipPage = {
  items: Clip[]
  nextOffset: number | null
  etag: string
  revision: string
  notModified?: boolean
}
type ClipListCacheEntry = {
  items: Clip[]
  etag: string
  revision: string
  inFlight?: Promise<Clip[]>
}

const CLIP_PAGE_SIZE = 100
const MAX_CLIP_PAGES = 10_000
const MAX_CLIP_LIST_CACHE_ENTRIES = 32
const clipListCache = new Map<string, ClipListCacheEntry>()

function clearClipListCache() {
  clipListCache.clear()
}

function trimClipListCache() {
  while (clipListCache.size > MAX_CLIP_LIST_CACHE_ENTRIES) {
    const candidate = [...clipListCache.keys()].find((key) => !clipListCache.get(key)?.inFlight)
    if (!candidate) return
    clipListCache.delete(candidate)
  }
}

function clipListKey(params: ClipListParams) {
  const origin = typeof window === 'undefined' ? '' : window.location.origin
  return JSON.stringify([origin, params.status || '', params.q || '', params.source_id || ''])
}

function clipListPath(params: ClipListParams, offset: number) {
  const search = new URLSearchParams({ limit: String(CLIP_PAGE_SIZE), offset: String(offset) })
  if (params.status) search.set('status', params.status)
  if (params.q) search.set('q', params.q)
  if (params.source_id) search.set('source_id', params.source_id)
  return `/api/clips?${search.toString()}`
}

async function readClipPage(params: ClipListParams, offset: number, etag = ''): Promise<ClipPage> {
  const path = clipListPath(params, offset)
  const headers = new Headers({ Accept: 'application/json' })
  if (etag) headers.set('If-None-Match', etag)
  const response = await fetch(path, { credentials: 'include', headers })
  if (response.status === 304) {
    return { items: [], nextOffset: null, etag, revision: '', notModified: true }
  }
  const raw = await response.text()
  let payload: unknown = null
  if (raw) {
    try { payload = JSON.parse(raw) } catch { payload = raw }
  }
  if (!response.ok) {
    await notifyAuthExpiry(path, response)
    const details = errorDetails(payload)
    const detail = details.message || `Request failed (${response.status})`
    throw new ApiRequestError(detail, response.status, { code: details.code, retryAfter: retryAfterSeconds(response) })
  }
  const envelope = typeof payload === 'object' && payload && !Array.isArray(payload) ? payload as { items?: unknown; next_offset?: unknown } : null
  const items = Array.isArray(payload) ? payload as Clip[] : Array.isArray(envelope?.items) ? envelope.items as Clip[] : []
  const headerNext = response.headers.get('X-Motion-Clip-Next-Offset')
  const envelopeNext = envelope?.next_offset
  const parsedNext = headerNext !== null ? Number(headerNext) : typeof envelopeNext === 'number' ? envelopeNext : NaN
  return {
    items,
    nextOffset: Number.isSafeInteger(parsedNext) && parsedNext >= 0 ? parsedNext : null,
    etag: response.headers.get('ETag') || '',
    revision: response.headers.get('X-Motion-Clip-Revision') || '',
  }
}

async function loadAllClipPages(params: ClipListParams, previous?: ClipListCacheEntry): Promise<{ items: Clip[]; etag: string; revision: string }> {
  // Bounded retries also keep the last complete cache usable during sustained
  // inventory writes: callers surface an error instead of committing partial data.
  for (let attempt = 0; attempt < 3; attempt++) {
    const first = await readClipPage(params, 0, previous?.etag)
    if (first.notModified && previous) return previous
    let changed = false
    const byId = new Map<string, Clip>()
    for (const item of first.items) byId.set(item.id, item)
    let nextOffset = first.nextOffset
    let previousOffset = 0
    const seenOffsets = new Set([0])
    let pageCount = 1
    while (nextOffset !== null) {
      if (nextOffset <= previousOffset || seenOffsets.has(nextOffset) || pageCount >= MAX_CLIP_PAGES) {
        throw new Error('The clip inventory returned an invalid page cursor.')
      }
      seenOffsets.add(nextOffset)
      previousOffset = nextOffset
      const page = await readClipPage(params, nextOffset)
      // Each backend page is internally consistent. Across requests, mutations
      // can reorder offsets; never publish a combined inventory from two revisions.
      if (first.revision && page.revision !== first.revision) {
        changed = true
        break
      }
      for (const item of page.items) byId.set(item.id, item)
      nextOffset = page.nextOffset
      pageCount += 1
    }
    if (!changed) return { items: [...byId.values()], etag: first.etag, revision: first.revision }
  }
  throw new Error('The clip inventory changed while loading. Retrying on the next poll.')
}

async function loadClipList(params: ClipListParams): Promise<Clip[]> {
  const key = clipListKey(params)
  const previous = clipListCache.get(key)
  if (previous?.inFlight) return previous.inFlight
  const entry: ClipListCacheEntry = previous || { items: [], etag: '', revision: '' }
  const pending = loadAllClipPages(params, previous).then((result) => {
    entry.items = result.items
    entry.etag = result.etag
    entry.revision = result.revision
    return result.items
  })
  entry.inFlight = pending
  clipListCache.set(key, entry)
  trimClipListCache()
  try {
    return await pending
  } finally {
    if (entry.inFlight === pending) delete entry.inFlight
    trimClipListCache()
  }
}

if (typeof window !== 'undefined') window.addEventListener(AUTH_EXPIRED_EVENT, clearClipListCache)

function json(body: unknown): RequestInit {
  return { method: 'POST', body: JSON.stringify(body) }
}

async function jobStatuses(targets: { job_ids?: string[]; source_ids?: string[]; clip_ids?: string[] }): Promise<Job[]> {
  const entries = (['job_ids', 'source_ids', 'clip_ids'] as const).flatMap(key =>
    [...new Set(targets[key] || [])].map(id => ({ key, id })))
  if (!entries.length) return []
  const jobs = new Map<string, Job>()
  // Bound each server read while retaining every visible resource. This also
  // supports long assistant sessions and clip lists larger than one page.
  for (let offset = 0; offset < entries.length; offset += 100) {
    const body: { job_ids: string[]; source_ids: string[]; clip_ids: string[] } = { job_ids: [], source_ids: [], clip_ids: [] }
    for (const { key, id } of entries.slice(offset, offset + 100)) body[key].push(id)
    for (const job of await request<Job[]>('/api/jobs/status', { method: 'POST', body: JSON.stringify(body) })) jobs.set(job.id, job)
  }
  return [...jobs.values()].sort((a, b) => b.updated_at.localeCompare(a.updated_at) || b.id.localeCompare(a.id))
}

export type ReturnReadiness = { configured: boolean; ready: boolean; root?: string; share_id?: string; message: string; recent_returns?: { status: string; message: string }[]; recent_handoffs?: { status: string; message: string }[] }
export type ProductionSettings = { media_dir: string | null; assets_dir: string | null; expected_isos: number; media_available: boolean; assets_available: boolean; revision: string; lower_third_overrides?: Record<string, string> }
export type ProductionSettingsInput = Pick<ProductionSettings, 'media_dir' | 'assets_dir' | 'expected_isos' | 'revision' | 'lower_third_overrides'>
export type ProductionFolderInspection = { media_candidates: { path: string; label: string }[]; camera_roles: string[]; recording_count: number; graphics_files: string[]; issues: string[] }

export const api = {
  reconcileLibrarySprout: (id: string, job_id: string, publishing_post_ids: string[]) => request<SproutReceipt>(`/api/library/${encodeURIComponent(id)}/sprout/reconcile`, json({ job_id, publishing_post_ids })),
  library: () => request<import('./types').LibraryAsset[]>('/api/library'),
  deleteLibraryAsset: (id: string) => request<{ id: string; deleted: boolean }>(`/api/library/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  restoreLibraryAsset: (id: string) => request<{ id: string; deleted: boolean }>(`/api/library/${encodeURIComponent(id)}/restore`, json({})),
  uploadLibrary: (file: File, title: string, description: string, onProgress?: (fraction: number) => void, signal?: AbortSignal) => {
    const body = new FormData(); body.append('file', file); body.append('title', title); body.append('description', description)
    return request<import('./types').LibraryAsset>('/api/library/upload', { method: 'POST', body, signal }, onProgress)
  },
  libraryEditorial: (id: string, values: { revision: number; planned_day: string | null; position: number | null }) => request<import('./types').LibraryAsset>(`/api/library/${encodeURIComponent(id)}/editorial`, { ...json(values), method: 'PUT' }),
  libraryDescription: (id: string, values: { revision: number; description: string }) => request<import('./types').LibraryAsset>(`/api/library/${encodeURIComponent(id)}/description`, { ...json(values), method: 'PUT' }),
  libraryPost: (id: string, values: import('./types').PublishingPlan) => request<import('./types').LibraryAsset>(`/api/library/${encodeURIComponent(id)}/publishing-plan`, { ...json(values), method: 'PUT' }),
  libraryDraft: (id: string, revision: number, request_id?: string, previous_job_id?: string | null) => request<import('./types').LibraryAsset>(`/api/library/${encodeURIComponent(id)}/sprout-draft`, json({ revision, request_id, previous_job_id })),
  captionGuidance: () => request<import('./types').CaptionGuidance>('/api/library/caption-context'),
  saveCaptionGuidance: (values: import('./types').CaptionGuidance) => request<import('./types').CaptionGuidance>('/api/library/caption-context', { ...json(values), method: 'PUT' }),
  captionDraft: (values: { asset_id?: string; clip_id?: string; version_id?: string; final_description?: string }) => request<{ caption: string }>('/api/library/caption', json(values)),
  sharedReturns: () => request<ReturnReadiness>('/api/desktop/returns'),
  productionSettings: () => request<ProductionSettings>('/api/desktop/production-settings'),
  inspectProductionFolders: (values: Omit<ProductionSettingsInput, 'lower_third_overrides'>) => request<ProductionFolderInspection>('/api/desktop/production-settings', { method: 'POST', body: JSON.stringify(values) }),
  saveProductionSettings: (values: ProductionSettingsInput) => request<ProductionSettings>('/api/desktop/production-settings', { method: 'PUT', body: JSON.stringify(values) }),
  saveSharedReturns: (root: string, share_id: string) => request<ReturnReadiness>('/api/desktop/returns', { method: 'PUT', body: JSON.stringify({ root, share_id }) }),
  clearSharedReturns: () => request<ReturnReadiness>('/api/desktop/returns', { method: 'DELETE' }),
  uploadProfilePhoto: (file: Blob) => {
    const body = new FormData()
    body.append('file', file, 'profile.jpg')
    return request<User>('/api/me/photo', { method: 'POST', body })
  },
  removeProfilePhoto: () => request<User>('/api/me/photo', { method: 'DELETE' }),
  bootstrap: () => request<Bootstrap>('/api/bootstrap'),
  setup: async (body: { name: string; email: string; password: string }) => {
    const result = await request<User>('/api/setup', json(body))
    clearClipListCache()
    return result
  },
  login: async (body: { email: string; password: string }) => {
    const result = await request<User>('/api/login', json(body))
    clearClipListCache()
    return result
  },
  logout: async () => {
    const result = await request<{ ok: boolean }>('/api/logout', { method: 'POST' })
    clearClipListCache()
    return result
  },
  assistantHistory: (id: string) => request<AssistantConversation>(`/api/assistant/conversations/${encodeURIComponent(id)}`),
  assistantConversation: () => request<AssistantConversation>('/api/assistant/conversations', { method: 'POST' }),
  assistant: (body: { conversation_id: string; turn_id: string; prompt: string; page: string; current_clip_id?: string | null }) => request<{ conversation_id: string; turn_id: string }>('/api/assistant', json(body)),
  assistantCancel: (conversation: string, turn: string) => request<{ status: string }>(`/api/assistant/conversations/${conversation}/turns/${turn}/cancel`, { method: 'POST' }),
  assistantConfirm: (conversation: string, turn: string, nonce: string) => request<{
    status: string
    nonce: string
    action: string
    summary?: string
    result?: Record<string, unknown>
    job?: Pick<Job, 'id' | 'kind' | 'status' | 'progress' | 'message'>
  }>(`/api/assistant/conversations/${encodeURIComponent(conversation)}/turns/${encodeURIComponent(turn)}/actions/${encodeURIComponent(nonce)}/confirm`, { method: 'POST' }),
  assistantStream: async (conversation: string, turn: string, after: number, signal: AbortSignal, onEvent: (event: AssistantEvent) => void) => {
    const response = await fetch(`/api/assistant/conversations/${conversation}/turns/${turn}/stream?after=${after}`, { credentials: 'include', signal })
    await readAssistantStream(response, onEvent)
  },
  authVerify: async (body: { email: string; token: string }) => {
    const result = await request<User>('/api/auth/verify', json(body))
    clearClipListCache()
    return result
  },
  authCode: (body: { email: string }) => request<{ ok: boolean }>('/api/auth/code', json(body)),
  authSession: async (access_token: string) => {
    const result = await request<User>('/api/auth/session', json({ access_token }))
    clearClipListCache()
    return result
  },

  multicamProjects: () => request<MulticamProject[]>('/api/multicam/projects'),
  createMulticamProject: (body: { title: string; speaker: string }) => request<MulticamProject>('/api/multicam/projects', json(body)),
  multicamProject: (id: string) => request<MulticamProject>(`/api/multicam/projects/${encodeURIComponent(id)}`),
  renameMulticamProject: (id: string, title: string) => request<MulticamProject>(`/api/multicam/projects/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ title }) }),
  deleteMulticamProject: (id: string) => request<{ deleted: boolean }>(`/api/multicam/projects/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  restoreMulticamProject: (id: string) => request<MulticamProject>(`/api/multicam/projects/${encodeURIComponent(id)}/restore`, { method: 'POST' }),
  startMulticamProject: (id: string) => request<MulticamProject>(`/api/multicam/projects/${encodeURIComponent(id)}/start`, { method: 'POST' }),
  syncMulticamProject: (id: string) => request<MulticamProject>(`/api/multicam/projects/${encodeURIComponent(id)}/sync`, { method: 'POST' }),
  uploadMulticamAngle: async (id: string, file: File, label: string, captureConfig?: CaptureConfig | null, onProgress?: (fraction: number) => void, signal?: AbortSignal) => {
    const direct = await uploadDirect<MulticamProject>(request, file, { purpose: 'angle', project_id: id, label, ...(captureConfig ? { capture_config: captureConfig } : {}) }, onProgress, signal)
    if (direct) return direct
    const body = new FormData()
    body.append('file', file)
    body.append('label', label)
    if (captureConfig) body.append('capture_config', JSON.stringify(captureConfig))
    return request<MulticamProject>(`/api/multicam/projects/${encodeURIComponent(id)}/angles`, { method: 'POST', body, signal }, onProgress)
  },
  removeMulticamAngle: (id: string, angleId: string) => request<MulticamProject>(`/api/multicam/projects/${encodeURIComponent(id)}/angles/${encodeURIComponent(angleId)}`, { method: 'DELETE' }),
  updateMulticamOffset: (id: string, angleId: string, offset_s: number) => request<MulticamProject>(`/api/multicam/projects/${encodeURIComponent(id)}/angles/${encodeURIComponent(angleId)}`, { method: 'PATCH', body: JSON.stringify({ offset_s }) }),
  multicamAngleSourceUrl: (id: string, angleId: string) => `/api/multicam/projects/${encodeURIComponent(id)}/angles/${encodeURIComponent(angleId)}/source`,
  multicamPremiereUrl: (id: string) => `/api/multicam/projects/${encodeURIComponent(id)}/premiere`,

  // Fetch the complete filtered inventory, but only walk older pages when the
  // server's snapshot token changes. Routine polling therefore reads one
  // bounded page and receives 304 while the cached older pages stay usable.
  clips: (params: ClipListParams = {}) => loadClipList(params),
  clip: (id: string) => request<Clip>(`/api/clips/${id}`),
  editorial: (id: string, body: Partial<Omit<EditorialPlan, 'assigned_editor'>> & { revision: number; assigned_editor_id?: string | null }) => request<Clip>(`/api/clips/${id}/editorial`, { method: 'PATCH', body: JSON.stringify(body) }),
  editContext: (id: string, version: string) => request<EditContext>(`/api/clips/${id}/edit-context?version_id=${encodeURIComponent(version)}`),
  clock: (sourceId?: string) => request<{ server_seconds: number; frame_clock?: boolean }>(sourceId ? `/api/clock?source_id=${encodeURIComponent(sourceId)}` : '/api/clock'),
  moments: (id: string) => request<SourceMoment[]>(`/api/sources/${id}/moments`),
  updateMoment: (sourceId: string, id: string, body: { note: string; previous_note: string }) => request<SourceMoment>(`/api/sources/${sourceId}/moments/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  markMoment: (id: string, body: { id: string; source_seconds: number | null; note: string; clicked_at?: number; clock_uncertainty?: number; actor_id?: string }) => request<SourceMoment>(`/api/sources/${id}/moments`, json(body)),
  deleteMoment: (sourceId: string, id: string) => request<{ ok: boolean }>(`/api/sources/${encodeURIComponent(sourceId)}/moments/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  restoreMoment: (sourceId: string, id: string) => request<SourceMoment>(`/api/sources/${encodeURIComponent(sourceId)}/moments/${encodeURIComponent(id)}/restore`, { method: 'POST' }),
  deleteClip: (id: string) => request<{ ok: boolean }>(`/api/clips/${id}`, { method: 'DELETE' }),
  restoreClip: (id: string) => request<Clip>(`/api/clips/${id}/restore`, { method: 'POST' }),
  approve: (id: string, version_id: string) => request<Clip>(`/api/clips/${id}/approve`, json({ version_id })),
  reject: (id: string, version_id: string, reason?: string) => request<Clip>(`/api/clips/${id}/reject`, json({ version_id, reason })),
  release: (id: string, reason?: string) => request<Clip>(`/api/clips/${id}/release`, json({ reason })),
  premiere: (id: string, version_id: string, refresh = false) => request<Delivery | Job>(`/api/clips/${id}/premiere`, json({ version_id, refresh })),
  preparedProjects: () => request<{ projects: PreparedProjectRegistration[] }>('/api/premiere/prepared-projects'),
  inspectPreparedProject: (sequence_id?: string) => request<PreparedProjectInspection>(`/api/premiere/prepared-project/inspect${sequence_id ? `?sequence_id=${encodeURIComponent(sequence_id)}` : ''}`),
  registerPreparedProject: (body: PreparedProjectRegistrationInput) => request<PreparedProjectRegistration>('/api/premiere/prepared-projects', json(body)),
  delivery: (id: string) => request<Delivery>(`/api/deliveries/${id}`),
  deliveries: () => request<Delivery[]>('/api/connector/deliveries'),
  savePublishingPlan: (id: string, plan: import('./types').PublishingPlan) => request<Clip>(`/api/clips/${encodeURIComponent(id)}/publishing-plan`, { ...json(plan), method: 'PUT' }),
  finalApprove: (id: string, version_id: string) => request<Clip>(`/api/clips/${id}/final-approve`, json({ version_id })),
  sendClipSproutDraft: (id: string, version_id: string, revision: number, request_id?: string, previous_job_id?: string | null) => request<Clip>(`/api/clips/${encodeURIComponent(id)}/sprout-draft`, json({ version_id, revision, request_id, previous_job_id })),
  sproutDeliveries: (id: string) => request<{ deliveries: SproutReceipt[] }>(`/api/clips/${encodeURIComponent(id)}/sprout`),
  retrySprout: (id: string, job_id: string) => request<Job>(`/api/clips/${encodeURIComponent(id)}/sprout/retry`, json({ job_id })),
  reconcileSprout: (id: string, job_id: string, publishing_post_ids: string[]) => request<SproutReceipt>(`/api/clips/${encodeURIComponent(id)}/sprout/reconcile`, json({ job_id, publishing_post_ids })),
  changes: (id: string, body: { version_id: string; note: string }) => request<Clip>(`/api/clips/${id}/changes`, json(body)),
  comments: (id: string) => request<Comment[]>(`/api/clips/${id}/comments`),
  addComment: (id: string, body: { text: string; version_id?: string }) => request<Comment>(`/api/clips/${id}/comments`, json(body)),
  uploadFinal: async (id: string, version_id: string, file: File, onProgress?: (fraction: number) => void, signal?: AbortSignal) => {
    const clip = await request<Clip>(`/api/clips/${id}`, { signal })
    const return_revision = clip.return_revision ?? 0
    const direct = await uploadDirect<Job>(request, file, { purpose: 'final', clip_id: id, version_id, return_revision }, onProgress, signal)
    if (direct) return direct
    const body = new FormData()
    body.append('version_id', version_id)
    body.append('return_revision', String(return_revision))
    body.append('file', file)
    return request<Clip>(`/api/clips/${id}/final`, { method: 'POST', body, signal }, onProgress)
  },

  sources: () => request<Source[]>('/api/sources'),
  sourceLibrary: () => request<Source[]>('/api/sources?include_projects=true'),
  renameSource: (id: string, title: string) => request<Source>(`/api/sources/${id}`, { method: 'PATCH', body: JSON.stringify({ title }) }),
  renameSpeaker: (sourceId: string, speaker_id: string, name: string) => request<{ speaker_id: string; name: string }>(`/api/sources/${sourceId}/speakers`, { method: 'PATCH', body: JSON.stringify({ speaker_id, name }) }),
  deleteSource: (id: string) => request<{ ok: boolean }>(`/api/sources/${id}`, { method: 'DELETE' }),
  restoreSource: (id: string) => request<Source>(`/api/sources/${id}/restore`, { method: 'POST' }),
  liveSource: (id: string) => request<LiveMonitor>(`/api/sources/${id}/live`),
  captureCapabilities: () => request<CaptureCapabilities>('/api/capture/capabilities'),
  uploadSource: async (file: File, title: string, context: string, onProgress?: (fraction: number) => void, captureConfig?: CaptureConfig | null, signal?: AbortSignal) => {
    const uploadRequest = { purpose: 'source' as const, title, context, ...(captureConfig ? { capture_config: captureConfig } : {}) }
    const direct = await uploadDirect<Source>(request, file, uploadRequest, onProgress, signal)
    if (direct) return direct
    const body = new FormData()
    body.append('file', file)
    body.append('title', title)
    body.append('context', context)
    if (captureConfig) body.append('capture_config', JSON.stringify(captureConfig))
    return request<Source>('/api/sources/upload', { method: 'POST', body, signal }, onProgress)
  },
  createSource: (body: { title: string; kind: 'url' | 'replay'; url?: string; source_id?: string; context?: string; max_seconds?: number | null; capture_mode?: CaptureMode; capture_input?: DeckLinkInput; capture_config?: CaptureConfig | null }) => {
    const path = body.capture_mode === 'desktop' ? '/api/desktop/capture/start' : '/api/sources'
    // The desktop relay knows its own capture mode. Keep the request body to
    // the normal SourceInput fields so a local lease/session is never exposed
    // to the renderer or accidentally forwarded as a cloud capture option.
    const input = body.capture_mode === 'desktop' ? (({ capture_mode: _captureMode, ...fields }) => fields)(body) : body
    return request<Source>(path, json(input))
  },
  retryDesktopCapture: (id: string) => request<Source>(`/api/desktop/capture/${encodeURIComponent(id)}/retry`, { method: 'POST' }),
  stopSource: (id: string) => request<{ ok: boolean }>(`/api/sources/${id}/stop`, { method: 'POST' }),
  analyzeSource: (id: string, max_clips?: number) => request<Job>(`/api/sources/${id}/analyze`, json({ max_clips })),
  uploadTranscript: (id: string, transcript: Transcript) => request<Source>(`/api/sources/${id}/transcript`, { method: 'POST', body: JSON.stringify(transcript) }),

  jobStatuses,
  jobs: (sourceId?: string) => sourceId ? jobStatuses({ source_ids: [sourceId] }) : request<Job[]>('/api/jobs'),
  clipJobs: (clipId: string) => jobStatuses({ clip_ids: [clipId] }),
  retryJob: (id: string) => request<Job>(`/api/jobs/${id}/retry`, { method: 'POST' }),
  events: () => request<Event[]>('/api/events'),
  settings: () => request<Settings>('/api/settings'),
  sproutDestinations: () => request<import('./types').SproutDestinations>('/api/sprout/destinations'),
  updateSettings: (body: Partial<Settings>) => request<Settings>('/api/settings', { method: 'PATCH', body: JSON.stringify(body) }),
  users: () => request<User[]>('/api/users'),
  createUser: (body: { name: string; email: string; password?: string; role: User['role'] }) => request<User>('/api/users', json(body)),
  connectorToken: () => request<{ token: string }>('/api/connector-token', { method: 'POST' }),
  revokeConnectorTokens: () => request<{ ok: boolean }>('/api/connector-token/revoke', { method: 'POST' }),
}
